import React from "react";
import { Box, Group, Text } from "@mantine/core";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import styles from "./CheckoutPanel.module.css";

interface GiftCardRevealProps {
  /** The client's total spendable gift-card balance (sum of active cards). */
  available: number;
  /** How much of it will apply to this sale — min(available, remaining bill). */
  amountToApply: number;
}

/**
 * Read-only reveal for the Gift Card tender. Unlike PackageReveal there is no
 * per-card selection or steppers: the backend draws the client's balance
 * soonest-expiry-first across their active cards and caps at the bill, so the
 * UI only shows the available balance and the amount that will be applied.
 */
export default function GiftCardReveal({
  available,
  amountToApply,
}: GiftCardRevealProps) {
  const remaining = Math.max(0, available - amountToApply);
  return (
    <Box className={styles.redeemPanel}>
      <Box className={styles.redeemPanelHeader}>
        <Box component="span" className={styles.redeemPanelTitle}>
          Gift card balance
        </Box>
      </Box>
      <Group justify="space-between" px="xs" py={4}>
        <Text size="sm" c="dimmed">
          Available
        </Text>
        <Text size="sm" fw={600}>
          {formatCurrency(available)}
        </Text>
      </Group>
      <Group justify="space-between" px="xs" py={4}>
        <Text size="sm" c="dimmed">
          Applying to this sale
        </Text>
        <Text size="sm" fw={700} c="var(--brand-teal)">
          {formatCurrency(amountToApply)}
        </Text>
      </Group>
      {remaining > 0 && (
        <Text size="xs" c="dimmed" px="xs" pt={2}>
          {formatCurrency(remaining)} will remain on the client's balance.
        </Text>
      )}
    </Box>
  );
}
